import { FunctionsHttpError } from '@supabase/supabase-js'
import { LoaderCircle, TriangleAlert, X } from 'lucide-react'
import { useState } from 'react'

import { supabase } from '../../lib/supabase'

function EliminarEmpresaModal({ empresa, onClose, onEliminada }) {
  const nombreObjetivo = (
    empresa?.nombre_fantasia || empresa?.razon_social || ''
  ).trim()

  const [textoConfirmacion, setTextoConfirmacion] = useState('')
  const [eliminando, setEliminando] = useState(false)
  const [error, setError] = useState('')

  const coincide = textoConfirmacion.trim() === nombreObjetivo

  const cerrar = () => {
    if (eliminando) return
    onClose()
  }

  const eliminarEmpresa = async (event) => {
    event.preventDefault()

    if (eliminando || !coincide) return

    setError('')

    try {
      setEliminando(true)

      const { data, error: errorFuncion } =
        await supabase.functions.invoke('eliminar-empresa', {
          body: { empresa_id: empresa.id },
        })

      if (errorFuncion) {
        // Cuando la función responde con un código distinto de 2xx, el SDK
        // no expone el cuerpo JSON en `data`: hay que leerlo desde
        // `error.context` para mostrar el mensaje real en vez del genérico
        // "Edge Function returned a non-2xx status code".
        if (errorFuncion instanceof FunctionsHttpError) {
          const cuerpoError = await errorFuncion.context
            .json()
            .catch(() => null)

          throw new Error(
            cuerpoError?.error || 'No fue posible eliminar la empresa.'
          )
        }

        throw errorFuncion
      }

      if (data?.error) {
        throw new Error(data.error)
      }

      onEliminada?.(data?.empresa || empresa)
    } catch (errorEliminar) {
      console.error('Error al eliminar la empresa:', errorEliminar)

      setError(
        errorEliminar.message ||
          'No fue posible eliminar la empresa.'
      )
    } finally {
      setEliminando(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          cerrar()
        }
      }}
    >
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl sm:p-8">
        <div className="mb-6 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
              <TriangleAlert size={20} />
            </div>

            <h2 className="text-xl font-bold text-slate-900">
              Eliminar empresa definitivamente
            </h2>
          </div>

          <button
            type="button"
            onClick={cerrar}
            disabled={eliminando}
            aria-label="Cerrar"
            className="shrink-0 text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed"
          >
            <X size={22} />
          </button>
        </div>

        <form
          className="space-y-5"
          onSubmit={eliminarEmpresa}
        >
          <p className="text-sm text-slate-600">
            Esta acción es permanente.
          </p>

          <div>
            <label
              htmlFor="confirmacion_empresa"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              Para confirmar escribe el nombre de la empresa:{' '}
              <span className="font-mono text-slate-900">
                {nombreObjetivo}
              </span>
            </label>

            <input
              id="confirmacion_empresa"
              type="text"
              value={textoConfirmacion}
              onChange={(event) =>
                setTextoConfirmacion(event.target.value)
              }
              disabled={eliminando}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none transition focus:border-red-500 focus:ring-2 focus:ring-red-100 disabled:bg-slate-100"
            />
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={cerrar}
              disabled={eliminando}
              className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={eliminando || !coincide}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300 sm:w-auto"
            >
              {eliminando && (
                <LoaderCircle
                  size={18}
                  className="animate-spin"
                />
              )}

              {eliminando ? 'Eliminando...' : 'Eliminar definitivamente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EliminarEmpresaModal
